import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useGLTF, Environment, Center } from "@react-three/drei";
import { useSpring, animated } from "@react-spring/three";
import { useRef, useEffect } from "react";
import * as THREE from "three";

function FloatingText({ onNameDragChange }) {
  const { scene } = useGLTF("/TEXT.glb");
  const meshRef = useRef();
  const isDraggingRef = useRef(false);
  const isHoveredRef = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const rotRef = useRef({ x: 0, y: 0 });
  const { gl } = useThree();

  const [spring, api] = useSpring(() => ({
    rotX: 0,
    rotY: 0,
    config: { mass: 1.5, tension: 120, friction: 28 },
  }));

  useEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.material = new THREE.MeshStandardMaterial({
          color: "#e8e8e8",
          metalness: 1,
          roughness: 0.08,
          envMapIntensity: 1,
        });
      }
    });
  }, [scene]);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (!isDraggingRef.current) {
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.6) * 0.12;
      meshRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.4) * 0.06;
      meshRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.8) * 0.08;
    }
  });

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDraggingRef.current) return;

      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      lastMouse.current = { x: e.clientX, y: e.clientY };

      rotRef.current.x -= dy * 0.005;
      rotRef.current.y -= dx * 0.005;
      api.start({ rotX: rotRef.current.x, rotY: rotRef.current.y });
    };

    const onMouseUp = () => {
  if (!isDraggingRef.current) return;

  isDraggingRef.current = false;
  onNameDragChange?.(false);

  rotRef.current = { x: 0, y: 0 };
  api.start({ rotX: 0, rotY: 0 });
  gl.domElement.style.cursor = isHoveredRef.current ? "grab" : "default";
};

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [api, gl, onNameDragChange]);

  return (
    <animated.group rotation-x={spring.rotX} rotation-y={spring.rotY}>
      <group
        ref={meshRef}
        scale={1.2}
        onPointerEnter={() => {
          isHoveredRef.current = true;
          gl.domElement.style.cursor = "grab";
        }}
        onPointerLeave={() => {
          isHoveredRef.current = false;
          if (!isDraggingRef.current) gl.domElement.style.cursor = "default";
        }}
        // 
        onPointerDown={(e) => {
          e.stopPropagation();
          isDraggingRef.current = true;
          onNameDragChange?.(true);
          lastMouse.current = { x: e.clientX, y: e.clientY };
          gl.domElement.style.cursor = "grabbing";
        }}
      >
        <primitive object={scene} />
      </group>
    </animated.group>
  );
}

function WelcomeText() {
  const { scene } = useGLTF("/Welcome.glb");
  const meshRef = useRef();
  const baseY = -0.2;

  useEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.material = new THREE.MeshStandardMaterial({
          color: "#c097cd",
          emissive: "#000000",
          emissiveIntensity: 0.18,
          metalness: 0.8,
          roughness: 0.5,
          envMapIntensity: 0.8,
        });
      }
    });
  }, [scene]);

  useFrame((state) => {
    if (!meshRef.current) return;
    meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.03;
    meshRef.current.position.y =
      baseY + Math.sin(state.clock.elapsedTime * 0.7) * 0.015;
  });

  return (
    <group ref={meshRef} position={[0, baseY, 0]} rotation={[Math.PI / 2, 0, 0]}>
      <Center>
        <primitive object={scene} scale={0.62} />
      </Center>
    </group>
  );
}

function BriefText() {
  const { scene } = useGLTF("/Brief.glb");
  const meshRef = useRef();
  const baseY = -1.25;

  useEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.material = new THREE.MeshStandardMaterial({
          color: "#7087b1",
          emissive: "#102030",
          emissiveIntensity: 0.12,
          metalness: 0.5,
          roughness: 0.3,
          envMapIntensity: 0.5,
        });
      }
    });
  }, [scene]);

  useFrame((state) => {
    if (!meshRef.current) return;

    meshRef.current.position.y =
      baseY + Math.sin(state.clock.elapsedTime * 0.85) * 0.025;
    meshRef.current.rotation.y =
      Math.sin(state.clock.elapsedTime * 0.45) * 0.015;
  });

  return (
    <group ref={meshRef} position={[0, baseY, 0]} rotation={[Math.PI / 2, 0, 0]}>
      <Center>
        <primitive object={scene} scale={0.3} />
      </Center>
    </group>
  );
}

export default function Home3D({ onNameDragChange }) {
  return (
    <section className="home3d-section" data-section="home">
  <div className="home3d-canvas-wrap">
        <Canvas
          camera={{ position: [0, 0, 5], fov: 45 }}
          style={{ width: "100%", height: "100%" }}
        >
          <ambientLight intensity={3.2} color="#ffffff" />
          <directionalLight position={[5, 5, 5]} intensity={2.8} color="#ffffff" />
          <directionalLight position={[-5, 5, -5]} intensity={2.2} color="#ffffff" />
          <directionalLight position={[0, -5, 5]} intensity={1.6} color="#ffffff" />
          <Environment preset="apartment" background={false} />

          <FloatingText onNameDragChange={onNameDragChange} />
          <WelcomeText />
          <BriefText />
  
        </Canvas>
        <p className="home3d-corner-note">
          (Click my name and move cursor to experience 3D world.)
          <br/> (Click on any skill in Skills section below to hear a piano note.)
        </p>
      </div>
    </section>
  );
}